import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail } from "@/components/ui/sidebar"
import { MessagesSquare, Logs, Plus, Network, Search, Copy, Check } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Input } from "@/components/ui/input"
import { usePermissions } from "@/lib/hooks/usePermissions"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { useUser } from "@/lib/hooks/useUser"
import { pages as declaredPages, groups as declaredGroups } from "@/lib/pages"

export function AppSidebar() {
  const navigate = useNavigate()
  const { canRead, loading: permissionsLoading } = usePermissions()
  const [searchQuery, setSearchQuery] = useState("")
  const [copiedTeamId, setCopiedTeamId] = useState<string | null>(null)
  const [dropdownWidth, setDropdownWidth] = useState<number>(0)
  const sidebarHeaderRef = useRef<HTMLDivElement>(null)
  const { data: user, isLoading: userLoading } = useUser()
  const { selectedTeam, setSelectedTeam } = useTeamStore()

  useEffect(() => {
    if (user?.teams && user.teams.length > 0 && !selectedTeam) {
      setSelectedTeam(user.teams[0])
    }
  }, [user, selectedTeam, setSelectedTeam])

  useEffect(() => {
    if (sidebarHeaderRef.current) {
      setDropdownWidth(sidebarHeaderRef.current.offsetWidth)
    }
  }, [])

  const filteredTeams = user?.teams?.filter(team =>
    team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    team.description?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || []

  const handleTeamSelect = (team: any) => {
    setSelectedTeam(team)
  }

  const handleCopyTeamId = (e: React.MouseEvent, teamId: string) => {
    e.stopPropagation()
    navigator.clipboard.writeText(teamId)
    setCopiedTeamId(teamId)
    setTimeout(() => setCopiedTeamId(null), 1500)
  }

  let pages = Object.values(declaredPages).filter((page: PageConfig) => !page.group && page.url).map(page => ({
    title: page.title,
    url: page.url,
    icon: page.icon,
    isLink: true,
    resource: page.resource,
    show: !permissionsLoading && canRead(page.resource)
  })) as any[]

  let groups = Object.entries(declaredGroups).map(([groupKey, group]: [string, Group]) => ({
    title: group.title,
    url: '#',
    icon: group.icon,
    items: Object.values(declaredPages).filter((page: PageConfig) => page.group === groupKey && page.url).map(page => ({
      title: page.title,
      url: page.url,
      icon: page.icon,
      isLink: true,
      resource: page.resource,
      show: !permissionsLoading && canRead(page.resource)
    })),
  })).filter(group => group.items.some(item => item.show))

  const isDev = user?.admin === true

  if (permissionsLoading) {
    pages = []
    groups = []
  }

  const prevNavItems = [].filter(item => {
    if (item.items) {
      item.items = item.items.filter(subItem => !permissionsLoading && canRead(subItem.permission))
      return item.items.length > 0
    }
    return item.show
  })

  if (!isDev) {
    groups = groups.filter(group => group.title !== "Dev")
    pages = pages.filter(page => page.url !== "/jobs")
  }

  const navItems = [...pages, ...prevNavItems, ...groups]

  const isLoading = userLoading || permissionsLoading

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarHeader ref={sidebarHeaderRef}>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="pl-1.5 pr-0 py-0 group-data-[collapsible=icon]:p-0! data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                    title="Select Workspace"
                  >
                    <div className="flex items-center justify-center bg-blue-100 rounded-[inherit] w-full h-full">
                      <span className="text-blue-600 font-semibold">
                        {isLoading ? '...' : selectedTeam?.name?.[0] || 'T'}
                      </span>
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {isLoading ? 'Loading...' : selectedTeam?.name || 'Select Team'}
                      </span>
                      <span className="truncate text-xs">
                        {isLoading ? 'Please wait' : selectedTeam?.description || 'Team Description'}
                      </span>
                    </div>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="min-w-(--sidebar-width) w-auto"
                  style={{ minWidth: dropdownWidth }}
                >
                  <div className="p-2">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search for a workspace"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  {filteredTeams.map((team) => (
                    <DropdownMenuItem
                      key={team.id}
                      onClick={() => handleTeamSelect(team)}
                      className={`flex items-center justify-between ${selectedTeam?.id === team.id ? "bg-accent" : ""}`}
                      title={`Switch to ${team.name}`}
                    >
                      <span>{team.name}</span>
                      <button
                        onClick={(e) => handleCopyTeamId(e, team.id)}
                        title="Copy team ID"
                        className="ml-2 p-0.5 rounded hover:bg-muted-foreground/20 transition-colors"
                      >
                        {copiedTeamId === team.id
                          ? <Check className="h-3.5 w-3.5 text-green-500" />
                          : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
                      </button>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate("/settings/teams/new")}
                    className="flex items-center gap-2"
                    title="Create New Workspace"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add a workspace</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/settings/teams")}
                    className="flex items-center gap-2"
                    title="Manage All Workspaces"
                  >
                    <Network className="h-4 w-4" />
                    <span>All workspaces</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <NavMain items={navItems} />
        </SidebarContent>
        <SidebarFooter>
          <NavUser user={user} />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
    </>
  )
}
